// Public document share page — shown at /share/:token to whoever the agent sent
// the link to (typically a carrier asking for a client's paperwork).
//
// Read-only mirror of DocPortalLandingPage: that one brings documents IN, this
// one hands them OUT. No auth — the token in the URL is the only credential, so
// the page never receives a permanent blob URL. Each Download button asks the
// API for a fresh 15-minute link at click time.
//
// The recipient is NOT a Deskte user and may open this on a phone, so it stays
// deliberately plain: who the documents belong to, the list, one button each.

import { useEffect, useState } from 'react';
import { FileText, Download, Shield, AlertCircle, Loader2 } from 'lucide-react';
import { viewDocSharePublic, getDocShareFileUrl } from '../api.js';

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtSize(bytes) {
  const b = Number(bytes) || 0;
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}

// The API answers with a machine code so the page can explain each dead end in
// the recipient's words instead of showing a raw status number.
const ERR_TEXT = {
  not_found: 'This link is not valid. Please ask the sender for a new one.',
  expired: 'This link has expired. Please ask the sender for a new one.',
  revoked: 'The sender has turned off access to this link.',
};

export function DocSharePage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [failed, setFailed] = useState(null);

  useEffect(() => {
    setLoading(true);
    viewDocSharePublic(token)
      .then(setData)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const download = async (file) => {
    setBusyId(file.id);
    setFailed(null);
    try {
      const { url, name } = await getDocShareFileUrl(token, file.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || file.name;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      // A link that died between page load and click (expired or revoked
      // meanwhile) reports the same codes as the initial GET.
      setFailed(ERR_TEXT[e.message] || 'Download failed. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Centered>Loading…</Centered>;

  if (err || !data) {
    return (
      <Centered>
        <AlertCircle size={40} style={{ color: '#ffb700', marginBottom: '1rem' }} />
        <div style={{ fontSize: '1rem', fontWeight: 700, color: '#eef6ff', marginBottom: '.5rem' }}>
          Link unavailable
        </div>
        <div style={{ fontSize: '.85rem', color: '#7aa3c0', maxWidth: 380, lineHeight: 1.6 }}>
          {ERR_TEXT[err] || 'This link is not available.'}
        </div>
      </Centered>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#070d14', padding: '2rem 1rem', fontFamily: 'system-ui,-apple-system,Segoe UI,sans-serif' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          {data.agencyLogoUrl
            ? <img src={data.agencyLogoUrl} alt={data.agencyName} style={{ maxHeight: 64, maxWidth: 240, objectFit: 'contain', marginBottom: '.75rem' }} />
            : null}
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#eef6ff' }}>{data.agencyName}</div>
          <div style={{ fontSize: '.72rem', color: '#45688a', marginTop: 2 }}>via Deskte</div>
        </div>

        <div style={{ background: '#0d1c2e', border: '1px solid #1a3550', borderRadius: 14, padding: '1.5rem' }}>
          <div style={{ fontSize: '.7rem', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: '#45688a', marginBottom: '.35rem' }}>
            Documents for
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#eef6ff', marginBottom: '1rem' }}>
            {data.clientName}
          </div>

          {data.message && (
            <div style={{ background: '#112538', border: '1px solid #1a3550', borderRadius: 10, padding: '.85rem 1rem', marginBottom: '1.25rem', fontSize: '.85rem', color: '#c8dff0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {data.message}
            </div>
          )}

          {failed && (
            <div style={{ background: 'rgba(255,61,107,.1)', border: '1px solid rgba(255,61,107,.3)', borderRadius: 10, padding: '.7rem .9rem', marginBottom: '1rem', fontSize: '.8rem', color: '#ff8fa8' }}>
              {failed}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
            {(data.files || []).map((f) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', background: '#112538', border: '1px solid #1a3550', borderRadius: 10, padding: '.75rem .9rem' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,212,255,.1)', border: '1px solid rgba(0,212,255,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={16} style={{ color: '#00d4ff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.85rem', fontWeight: 600, color: '#eef6ff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </div>
                  <div style={{ fontSize: '.7rem', color: '#45688a', marginTop: 2 }}>{fmtSize(f.sizeBytes)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => download(f)}
                  disabled={busyId === f.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '.35rem', background: busyId === f.id ? '#1e3a52' : 'rgba(0,212,255,.12)', border: '1px solid rgba(0,212,255,.3)', color: '#00d4ff', fontSize: '.78rem', fontWeight: 700, padding: '.5rem .9rem', borderRadius: 8, cursor: busyId === f.id ? 'wait' : 'pointer', flexShrink: 0, fontFamily: 'inherit' }}
                >
                  {busyId === f.id
                    ? <Loader2 size={14} style={{ animation: 'dkt-spin 1s linear infinite' }} />
                    : <Download size={14} />}
                  Download
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.5rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #1a3550' }}>
            <Shield size={14} style={{ color: '#45688a', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: '.72rem', color: '#45688a', lineHeight: 1.6 }}>
              This link expires on <strong style={{ color: '#7aa3c0' }}>{fmtDate(data.expiresAt)}</strong> and
              contains confidential client information. Please do not forward it.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: '#070d14', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', fontFamily: 'system-ui,-apple-system,Segoe UI,sans-serif', color: '#7aa3c0' }}>
      {children}
    </div>
  );
}
